import { Check } from "lucide-react";

type PriceRange = {
  label: string;
  min: number;
  max: number;
};

type ShopFilterSidebarProps = {
  className?: string;
  categories: string[];
  selectedCategory: string | null;
  selectedPriceIdx: number;
  activeFilterCount: number;
  priceRanges: PriceRange[];
  categoryCounts?: Map<string, number>;
  onSelectCategory: (category: string | null) => void;
  onSelectPrice: (priceIdx: number) => void;
  onClearFilters: () => void;
};

const ShopFilterSidebar = ({
  className = "",
  categories,
  selectedCategory,
  selectedPriceIdx,
  activeFilterCount,
  priceRanges,
  categoryCounts,
  onSelectCategory,
  onSelectPrice,
  onClearFilters,
}: ShopFilterSidebarProps) => (
  <aside className={className}>
    {/* Category */}
    <div className="mb-8">
      <h3 className="font-display font-semibold text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
        Category
      </h3>
      <div className="space-y-0.5">
        <button
          onClick={() => onSelectCategory(null)}
          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-body transition-all duration-200 ${
            !selectedCategory
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-foreground hover:bg-secondary/80"
          }`}
        >
          <span>All Categories</span>
          {!selectedCategory && <Check className="w-3.5 h-3.5" />}
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => onSelectCategory(cat)}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-body transition-all duration-200 ${
              selectedCategory === cat
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-foreground hover:bg-secondary/80"
            }`}
          >
            <span>{cat}</span>
            <span className="flex items-center gap-1.5">
              {categoryCounts && (
                <span className={`text-[10px] font-medium ${
                  selectedCategory === cat ? "text-primary-foreground/70" : "text-muted-foreground"
                }`}>
                  {categoryCounts.get(cat) || 0}
                </span>
              )}
              {selectedCategory === cat && <Check className="w-3.5 h-3.5" />}
            </span>
          </button>
        ))}
      </div>
    </div>

    {/* Divider */}
    <div className="h-px bg-border/60 mb-8" />

    {/* Price Range */}
    <div className="mb-8">
      <h3 className="font-display font-semibold text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
        Price Range
      </h3>
      <div className="space-y-0.5">
        {priceRanges.map((range, idx) => (
          <button
            key={range.label}
            onClick={() => onSelectPrice(idx)}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-body transition-all duration-200 ${
              selectedPriceIdx === idx
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-foreground hover:bg-secondary/80"
            }`}
          >
            <span>{range.label}</span>
            {selectedPriceIdx === idx && <Check className="w-3.5 h-3.5" />}
          </button>
        ))}
      </div>
    </div>

    {/* Clear */}
    {activeFilterCount > 0 && (
      <button
        onClick={onClearFilters}
        className="w-full py-2.5 rounded-lg border border-border text-sm font-body font-medium text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-all duration-200"
      >
        Clear all filters
      </button>
    )}
  </aside>
);

export default ShopFilterSidebar;
